import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  IncomingRequestForQuotationDto,
  IncomingRequestForQuotationState,
  UpdateRequestForQuotationRequest,
  UploadRequestForQuotationResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

export type IncomingRfq = IncomingRequestForQuotationDto
export { IncomingRequestForQuotationState as IncomingRfqState }

export const incomingRfqRequestIds = {
  list: 'ListIncomingRfqs',
  detail: (rfqId: string) => `GetIncomingRfq-${rfqId}`,
  upload: 'UploadIncomingRfq',
  download: (rfqId: string) => `DownloadIncomingRfq-${rfqId}`,
  setBuyerRelation: (rfqId: string) => `SetIncomingRfqBuyerRelation-${rfqId}`,
  createProject: (rfqId: string) => `CreateProjectFromIncomingRfq-${rfqId}`,
} as const

interface IncomingRfqsClientContract {
  list(): Promise<IncomingRequestForQuotationDto[]>
  detail(rfqId: string): Promise<IncomingRequestForQuotationDto>
  upload(file: File, correlationId?: string): Promise<UploadRequestForQuotationResponse>
  download(rfqId: string): Promise<boolean>
  setBuyerRelation(
    rfqId: string,
    request: UpdateRequestForQuotationRequest,
  ): Promise<void>
  createProject(rfqId: string): Promise<void>
}

export class IncomingRfqsClient
  extends InternalAPI
  implements IncomingRfqsClientContract
{
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(
      `/api/parties/${partyId}/request-for-quotations/incoming`,
      onRequestChange,
    )
  }

  public list(): Promise<IncomingRequestForQuotationDto[]> {
    return this.GetAsync({ relativePath: '', id: incomingRfqRequestIds.list })
  }

  public detail(rfqId: string): Promise<IncomingRequestForQuotationDto> {
    return this.GetAsync({
      relativePath: `/${rfqId}`,
      id: incomingRfqRequestIds.detail(rfqId),
    })
  }

  public upload(file: File, correlationId?: string): Promise<UploadRequestForQuotationResponse> {
    const formData = new FormData()
    formData.append('file', file)
    if (correlationId) formData.append('correlationId', correlationId)

    return this.PostAuthenticatedAsync({
      relativePath: '/upload',
      data: formData,
      id: incomingRfqRequestIds.upload,
    })
  }

  public download(rfqId: string) {
    return this.DownloadAsync({
      relativePath: `/${rfqId}/document`,
      id: incomingRfqRequestIds.download(rfqId),
    })
  }

  public setBuyerRelation(
    rfqId: string,
    request: UpdateRequestForQuotationRequest,
  ): Promise<void> {
    return this.PutAuthenticatedAsync({
      relativePath: `/${rfqId}`,
      data: request,
      id: incomingRfqRequestIds.setBuyerRelation(rfqId),
    })
  }

  public createProject(rfqId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${rfqId}/create-project`,
      id: incomingRfqRequestIds.createProject(rfqId),
    })
  }
}

export class IncomingRfqsController extends BaseController<IncomingRfqsClientContract> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new IncomingRfqsClient(partyId, onRequestChange)
    })
  }

  public async list(): Promise<IncomingRequestForQuotationDto[]> {
    try {
      return await this.api.list()
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async detail(rfqId: string): Promise<IncomingRequestForQuotationDto> {
    try {
      return await this.api.detail(rfqId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async upload(file: File, correlationId?: string): Promise<UploadRequestForQuotationResponse> {
    try {
      return await this.api.upload(file, correlationId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async download(rfqId: string) {
    try {
      return await this.api.download(rfqId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async setBuyerRelation(
    rfqId: string,
    buyerRelationId: string | undefined,
  ): Promise<void> {
    try {
      await this.api.setBuyerRelation(rfqId, { buyerRelationId })
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async createProject(rfqId: string): Promise<void> {
    try {
      await this.api.createProject(rfqId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }
}
